import {
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React from 'react';
import { useAuth } from '../contexts/AuthContext';

interface CEONavigationProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

const CEONavigation: React.FC<CEONavigationProps> = ({ currentPage, onNavigate }) => {
  const { user, logout } = useAuth();

  const navigationItems = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: ChartBarIcon,
      description: 'Overview and analytics'
    },
    {
      id: 'gyms',
      name: 'Enrolled Gyms',
      icon: BuildingOfficeIcon,
      description: 'Manage gym network'
    },
    {
      id: 'enrollment',
      name: 'Enrollment',
      icon: PlusCircleIcon,
      description: 'Add new gyms'
    }
  ];

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="flex flex-col flex-grow bg-white/10 backdrop-blur-lg border-r border-white/20">
      {/* Header */}
      <div className="flex h-16 items-center px-4 border-b border-white/20">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">C</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-white break-words leading-tight">
              Central Core+ CEO
            </h1>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {navigationItems.map((item) => {
          const isActive = currentPage === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={clsx(
                'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 w-full text-left',
                isActive
                  ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                  : 'text-gray-300 hover:bg-gradient-to-r hover:from-orange-500/10 hover:to-blue-500/10 hover:text-white'
              )}
            >
              <item.icon
                className={clsx(
                  'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                  isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'
                )}
              />
              <div>
                <div className="font-medium">{item.name}</div>
                <div className="text-xs opacity-75">{item.description}</div>
              </div>
            </button>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="px-4 py-3 border-t border-white/20">
        <div className="flex items-center gap-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-semibold">
              {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-gray-300 truncate">CEO</p>
          </div>
        </div>
      </div>

      {/* Logout button at bottom */}
      <div className="px-2 py-4 border-t border-white/20">
        <button
          onClick={handleLogout}
          className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-gray-300 hover:bg-red-500/20 hover:text-red-300 w-full text-left"
        >
          <ArrowRightOnRectangleIcon
            className="mr-3 h-5 w-5 flex-shrink-0 transition-colors text-gray-400 group-hover:text-red-300"
          />
          Logout
        </button>
      </div>
    </div>
  );
};

export default CEONavigation;
